import { mean, round } from 'lodash'
import {
  HourSerieCalculatorResult,
  InputHourSerieType,
} from '../building-energy-model/hour-series-calculator'
import {
  BuildingEnergyModelFragment,
  ConsumptionEnergyType,
  EnergySystemFragment,
  EnergySystemType,
  HourSeriesType,
  SpaceHeatingParameters,
} from '../generated/graphql'
import { calculateHourSerieFactors } from '../schedule/hour-series-factors-calculator'
import { calcHeatLoadHourSeries } from './heatload-hour-series-calculator'
import { hoursInYear } from '../util/hours-in-year'
import { i18n } from '../i18n'

const calcPrevious12Average = (numbers: number[], currentIndex: number) =>
  mean(numbers.slice(currentIndex - 12, currentIndex))

export const spaceHeatingCalculator = (
  outsideTempHourSerie: number[],
  ventilationHeatingDemandSerie: number[],
  heatLoadSupplySerie: number[],
  energySystem: EnergySystemFragment,
  year: number,
): HourSerieCalculatorResult => {
  const params = energySystem.parameters as SpaceHeatingParameters

  if (!energySystem.schedule)
    throw new Error('Schedule for space heating not found')

  const spaceHeatingSerie = calculateHourSerieFactors(
    energySystem.schedule,
    year,
  )

  // First calculate heat losses

  // Total area of exterior parts
  const totalArea = round(
    Number(params.exteriorWallArea) +
      Number(params.roofArea) +
      Number(params.baseFloorArea) +
      Number(params.windowsArea) +
      Number(params.doorsArea),
    1,
  )

  const heavyHeatLosses = round(
    params.exteriorWallArea * params.exteriorWallUvalue +
      params.roofArea * params.roofUvalue +
      params.baseFloorArea * params.baseFloorUvalue,
    1,
  )

  const lightExteriorHeatLosses = round(
    params.windowsArea * params.windowsUvalue +
      params.doorsArea * params.doorsUvalue,
    1,
  )

  const heatBrigdeLosses =
    params.heatBridgeRatio * (heavyHeatLosses + lightExteriorHeatLosses)

  const airLeakageLosses =
    (((params.q50 / params.airLeakageXfactor) * totalArea) / 3.6) * 1.2

  const lightHeatLosses = round(
    lightExteriorHeatLosses + heatBrigdeLosses + airLeakageLosses,
    1,
  )

  for (let i = 0; i < spaceHeatingSerie.length; i++) {
    if (spaceHeatingSerie[i] === 0) continue // eslint-disable-line

    const currTemp = outsideTempHourSerie[i]

    let outside12hTemp = currTemp

    if (i > 11) outside12hTemp = calcPrevious12Average(outsideTempHourSerie, i)

    const dynamicOutTemp =
      outside12hTemp * params.dynamicOutTempWeightFactor +
      currTemp * (1 - params.dynamicOutTempWeightFactor)

    const heavyHeatLossesPower = Math.max(
      0,
      round(
        ((params.affectingHeatLossStartTemp - dynamicOutTemp) *
          heavyHeatLosses) /
          1000,
        2,
      ),
    )

    const lightHeatLossesPower = Math.max(
      0,
      round(
        ((params.affectingHeatLossStartTemp - currTemp) * lightHeatLosses) /
          1000,
        2,
      ),
    )

    let heatLossesFactor = 1

    if (currTemp > params.heatLossesCorrectionFactorStartTemp) {
      heatLossesFactor = 0

      if (params.affectingHeatLossStartTemp !== currTemp) {
        heatLossesFactor = round(
          (params.affectingHeatLossStartTemp -
            params.heatLossesCorrectionFactorStartTemp +
            (params.heatLossesCorrectionFactorStartTemp - currTemp) *
              params.heatLossesCorrectionFactor) /
            (params.affectingHeatLossStartTemp - currTemp),
          3,
        )
      }
    }

    const deltaSupplyAirHeating = Math.min(
      params.supplyAirHeatingStartTemp - params.supplyAirFullHeatingStartTemp,
      Math.max(0, params.supplyAirFullHeatingStartTemp - currTemp),
    )

    const supplyAirHeatingFactor =
      deltaSupplyAirHeating /
      (params.supplyAirHeatingStartTemp - params.supplyAirFullHeatingStartTemp)

    const supplyAirHeatLossPower =
      supplyAirHeatingFactor * ventilationHeatingDemandSerie[i]

    const spaceHeatingPower = Math.max(
      0,
      round(
        (heavyHeatLossesPower + lightHeatLossesPower) * heatLossesFactor +
          supplyAirHeatLossPower -
          heatLoadSupplySerie[i],
        2,
      ),
    )

    spaceHeatingSerie[i] *=
      round(
        spaceHeatingPower / params.heatSupplyEfficiency / params.basEfficiency,
        2,
      ) * 1000
  }

  const result: HourSerieCalculatorResult = {
    outputs: [
      {
        hourSerieType: HourSeriesType.HeatingDemand,
        energySystemType: EnergySystemType.SpaceHeating,
        consumptionEnergyType: ConsumptionEnergyType.SpaceHeating,
        hourSerieName: i18n.t(
          'buildingEnergyModels.hourSeriesNames.spaceHeating',
        ),
        hourSerie: spaceHeatingSerie,
      },
    ],
    inputs: [],
  }

  return result
}

export const calcSpaceHeatingDensityHourSeries = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  ventilationHeatingDemandSerie: number[],
  area: number,
  year: number,
): number[] => {
  const defaultResult = Array<number>(hoursInYear).fill(0)

  const spaceHeating = model.energySystems.find(
    (s) => s.type === EnergySystemType.SpaceHeating,
  )

  if (!spaceHeating) return defaultResult

  const heatloadResult = calcHeatLoadHourSeries(
    model,
    outsideTempHourSerie,
    area,
    year,
  )

  const heatLoadSupplySerie =
    heatloadResult.inputs.find(
      (r) => r.inputSerieType === InputHourSerieType.HeatLoadHeatloadSupply,
    )?.hourSerie ?? Array<number>(hoursInYear).fill(0)

  const spaceHeatingHourSerieResult = spaceHeatingCalculator(
    outsideTempHourSerie,
    ventilationHeatingDemandSerie,
    heatLoadSupplySerie,
    spaceHeating,
    year,
  )

  const spaceHeatingSerie = spaceHeatingHourSerieResult.outputs
    .find((r) => r.hourSerieType === HourSeriesType.HeatingDemand)
    ?.hourSerie.map((i) => i / area)

  if (!spaceHeatingSerie) return defaultResult

  return spaceHeatingSerie
}
